import { SCHOOL_CLASS_MEMBER_ROLE } from '../types/graphql-types';
import { isOverAllocated } from './schoolWarnings';

export interface School {
  name: string;
  numLicenses: number;
  hasSufficientSeats: boolean;
  schoolContentAccess: {
    realm: { name: string } | null;
    numBuildingLicenses: number;
    maxSeats: number;
    schoolMembers: { account: { id: string } | null }[];
  }[];
}
export interface SchoolClass {
  name: string;
  teacherTrainingType: string | null;
  schoolClassMembers: { role: SCHOOL_CLASS_MEMBER_ROLE }[];
}

const schoolLicenseWarnings = (school: School): string[] => {
  const warnings = [];
  if (!school.numLicenses) {
    warnings.push('⚠️HAS_NO_LICENSES');
  }
  if (isOverAllocated(school)) {
    warnings.push('⚠️OVER_ALLOCATED');
  }
  return warnings;
};

const hasLicenseWarnings = (school: School): boolean =>
  schoolLicenseWarnings(school).length > 0;

export const hasAnyCurrentLicenses = (schools: School[] | null): boolean =>
  !!schools && schools.some((school) => school.numLicenses > 0);

const formatSchoolLicencesForIntercom = (school: School) => {
  const warningsText = hasLicenseWarnings(school)
    ? ` ${schoolLicenseWarnings(school).join(' ')}`
    : '';

  const schoolContentAccess = school.schoolContentAccess || [];
  const licensesText = schoolContentAccess
    .filter(({ realm }) => realm)
    .map(({ realm, numBuildingLicenses, maxSeats, schoolMembers }) => {
      const isExpired = !numBuildingLicenses && !maxSeats;
      const overAllocated =
        !numBuildingLicenses && maxSeats < schoolMembers.length;
      return `${realm?.name}${isExpired ? ' ⚠️EXPIRED' : ''}${
        overAllocated ? ' ⚠️OVER_ALLOCATED' : ''
      }`;
    })
    .join(', ');
  return `${school.name}${warningsText}: [${licensesText}]`;
};

export const formatSchoolsLicencesForIntercom = (schools: School[]): string => {
  const warningMessage = schools.some(hasLicenseWarnings)
    ? '⚠️ License issues detected in some schools! '
    : '';
  return `${warningMessage}${schools
    .map(formatSchoolLicencesForIntercom)
    .join(', ')}`;
};

export const formatStudentsPerClass = (classes: SchoolClass[]): string =>
  classes
    .filter((c) => !c.teacherTrainingType)
    .map(
      (c) =>
        `${c.name} [${
          c.schoolClassMembers.filter(
            (m) => m.role === SCHOOL_CLASS_MEMBER_ROLE.student
          ).length
        } students]`
    )
    .join(', ');

export const formatRealmAccess = (
  schools: School[],
  accountId: string
): string => {
  const realms = schools.reduce((acc: string[], school: School) => {
    if (!school.hasSufficientSeats) {
      return acc;
    }
    const realmNames = school.schoolContentAccess
      .filter(
        ({ numBuildingLicenses, schoolMembers, realm }) =>
          realm &&
          (numBuildingLicenses > 0 ||
            schoolMembers.some(({ account }) => account?.id === accountId))
      )
      .map(({ realm }) => realm!.name);
    return [...acc, ...realmNames];
  }, []);
  if (realms.length === 0) {
    return 'No access';
  }
  return [...new Set(realms)].join(', ');
};
